import {
    LS_FOLDER_ID, LS_FILE_ID, LS_FI_FILE_ID, LS_ADVISOR_FILE_ID,
    DRIVE_FOLDER_PATH, APPLICATION_FOLDER_ID,
    DRIVE_FILE_NAME, FI_REPORT_FILE_NAME, ADVISOR_REPORT_FILE_NAME
} from '../config.jsx';

// ── Drive REST helpers ────────────────────────────────────────
export const DriveAPI = {

    // In-flight promise lock — prevents concurrent calls from racing
    _resolvingFolder: null,

    // Resolve the iPlanner sub-folder inside the hardcoded Application folder.
    // Application folder ID is never searched — it's baked in as a constant.
    // Only the iPlanner sub-folder needs a one-time search/create.
    // Result persisted in localStorage so Drive API is called at most once ever.
    async ensureFolderPath(token) {
        // Return persisted ID immediately — zero API calls
        const cached = localStorage.getItem(LS_FOLDER_ID);
        if (cached) return cached;

        // Prevent concurrent resolution race
        if (this._resolvingFolder) return this._resolvingFolder;

        this._resolvingFolder = (async () => {
            // Application folder ID is hardcoded — never searched, never duplicated
            const iplannerName = DRIVE_FOLDER_PATH[1]; // 'iPlanner'
            const q = encodeURIComponent(
                `mimeType='application/vnd.google-apps.folder' ` +
                `and name='${iplannerName}' ` +
                `and '${APPLICATION_FOLDER_ID}' in parents ` +
                `and trashed=false`
            );
            const searchRes = await fetch(
                `https://www.googleapis.com/drive/v3/files` +
                `?q=${q}&fields=files(id,name,createdTime)&orderBy=createdTime&spaces=drive`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!searchRes.ok) throw new Error(`iPlanner folder search failed: ${searchRes.status}`);
            const { files } = await searchRes.json();

            let iplannerFolderId;
            if (files && files.length > 0) {
                iplannerFolderId = files[0].id;
                console.log(`[iPlanner] ✅ Found iPlanner folder id=${iplannerFolderId}`);
            } else {
                // Create iPlanner inside the hardcoded Application folder
                const createRes = await fetch(
                    'https://www.googleapis.com/drive/v3/files',
                    {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            name: iplannerName,
                            mimeType: 'application/vnd.google-apps.folder',
                            parents: [APPLICATION_FOLDER_ID],
                        }),
                    }
                );
                if (!createRes.ok) throw new Error(`iPlanner folder create failed: ${createRes.status}`);
                const created = await createRes.json();
                iplannerFolderId = created.id;
                console.log(`[iPlanner] 📁 Created iPlanner folder id=${iplannerFolderId}`);
            }

            // Persist forever — never call Drive search again for this folder
            localStorage.setItem(LS_FOLDER_ID, iplannerFolderId);
            console.log(`[iPlanner] 💾 iPlanner folder ID persisted: ${iplannerFolderId}`);
            return iplannerFolderId;
        })().finally(() => {
            this._resolvingFolder = null;
        });

        return this._resolvingFolder;
    },

    // Find iplanner-data-sync.json inside the resolved folder.
    // Persists the file ID in localStorage once found.
    async findFile(token) {
        // Return persisted file ID immediately if available
        const cachedId = localStorage.getItem(LS_FILE_ID);
        if (cachedId) return { id: cachedId };

        const folderId = await this.ensureFolderPath(token);
        const q = encodeURIComponent(
            `name='${DRIVE_FILE_NAME}' and '${folderId}' in parents and trashed=false`
        );
        const res = await fetch(
            `https://www.googleapis.com/drive/v3/files?q=${q}&fields=files(id,name,modifiedTime)&spaces=drive`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`Drive list failed: ${res.status}`);
        const { files } = await res.json();

        if (files && files.length > 0) {
            localStorage.setItem(LS_FILE_ID, files[0].id);
            return files[0];
        }
        return null;
    },

    // Download and parse the sync file content.
    async readFile(token, fileId) {
        const res = await fetch(
            `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`Drive read failed: ${res.status}`);
        return res.json();
    },

    // Write data to the sync file.
    // fileId provided → PATCH (update).  fileId null → multipart POST (create).
    async writeFile(token, fileId, data) {
        const body = JSON.stringify(data, null, 2);
        const boundary = 'iPlanner_mp_boundary_x7z';

        if (fileId) {
            const res = await fetch(
                `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`,
                {
                    method: 'PATCH',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body,
                }
            );
            if (!res.ok) throw new Error(`Drive update failed: ${res.status}`);
            return res.json();
        } else {
            const folderId = await this.ensureFolderPath(token);
            const metadata = {
                name: DRIVE_FILE_NAME,
                parents: [folderId],
                mimeType: 'application/json',
            };
            const multipart =
                `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
                `${JSON.stringify(metadata)}\r\n` +
                `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
                `${body}\r\n` +
                `--${boundary}--`;
            const res = await fetch(
                'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': `multipart/related; boundary=${boundary}`,
                    },
                    body: multipart,
                }
            );
            if (!res.ok) throw new Error(`Drive create failed: ${res.status}`);
            const created = await res.json();
            // Persist the new file ID immediately
            localStorage.setItem(LS_FILE_ID, created.id);
            return created;
        }
    },

    // Call this on sign-out to clear all persisted Drive IDs
    clearPersistedIds() {
        localStorage.removeItem(LS_FOLDER_ID);
        localStorage.removeItem(LS_FILE_ID);
        localStorage.removeItem(LS_FI_FILE_ID);
        localStorage.removeItem(LS_ADVISOR_FILE_ID);
    },

    // ── FI Report: find existing iplanner-fi-report.json ──────────
    async findFIReportFile(token) {
        const cachedId = localStorage.getItem(LS_FI_FILE_ID);
        if (cachedId) return { id: cachedId };

        const folderId = await this.ensureFolderPath(token);
        const q = encodeURIComponent(
            `name='${FI_REPORT_FILE_NAME}' and '${folderId}' in parents and trashed=false`
        );
        const res = await fetch(
            `https://www.googleapis.com/drive/v3/files?q=${q}&fields=files(id,name)&spaces=drive`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`FI report search failed: ${res.status}`);
        const { files } = await res.json();
        if (files && files.length > 0) {
            localStorage.setItem(LS_FI_FILE_ID, files[0].id);
            return files[0];
        }
        return null;
    },

    // Write (or overwrite) the FI report file in the iPlanner folder.
    // Always uses the fixed filename iplanner-fi-report.json.
    async writeFIReportFile(token, reportData) {
        const body = JSON.stringify(reportData, null, 2);
        const boundary = 'iPlanner_fi_boundary_k3p';

        // Try to find existing file first (uses cached ID if available)
        const existing = await this.findFIReportFile(token);

        if (existing) {
            // PATCH (overwrite content only)
            const res = await fetch(
                `https://www.googleapis.com/upload/drive/v3/files/${existing.id}?uploadType=media`,
                {
                    method: 'PATCH',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body,
                }
            );
            if (!res.ok) throw new Error(`FI report update failed: ${res.status}`);
            console.log('[iPlanner] ✅ FI report overwritten');
            return await res.json();
        } else {
            // POST (create new)
            const folderId = await this.ensureFolderPath(token);
            const metadata = {
                name: FI_REPORT_FILE_NAME,
                parents: [folderId],
                mimeType: 'application/json',
            };
            const multipart =
                `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
                `${JSON.stringify(metadata)}\r\n` +
                `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
                `${body}\r\n` +
                `--${boundary}--`;
            const res = await fetch(
                'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': `multipart/related; boundary=${boundary}`,
                    },
                    body: multipart,
                }
            );
            if (!res.ok) throw new Error(`FI report create failed: ${res.status}`);
            const created = await res.json();
            localStorage.setItem(LS_FI_FILE_ID, created.id);
            console.log(`[iPlanner] 📄 FI report created id=${created.id}`);
            return created;
        }
    },

    // Write (or overwrite) the Advisor report file in the iPlanner folder.
    // Always uses the fixed filename iplanner-fi-report-advisor.json.
    async writeAdvisorReportFile(token, reportData) {
        const body = JSON.stringify(reportData, null, 2);
        const boundary = 'iPlanner_adv_boundary_k3p';

        const cachedId = localStorage.getItem(LS_ADVISOR_FILE_ID);
        let existing = cachedId ? { id: cachedId } : null;

        if (!existing) {
            const folderId = await this.ensureFolderPath(token);
            const q = encodeURIComponent(
                `name='${ADVISOR_REPORT_FILE_NAME}' and '${folderId}' in parents and trashed=false`
            );
            const sr = await fetch(
                `https://www.googleapis.com/drive/v3/files?q=${q}&fields=files(id,name)&spaces=drive`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!sr.ok) throw new Error(`Advisor report search failed: ${sr.status}`);
            const { files } = await sr.json();
            if (files && files.length > 0) {
                localStorage.setItem(LS_ADVISOR_FILE_ID, files[0].id);
                existing = files[0];
            }
        }

        if (existing) {
            const res = await fetch(
                `https://www.googleapis.com/upload/drive/v3/files/${existing.id}?uploadType=media`,
                {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body,
                }
            );
            if (!res.ok) throw new Error(`Advisor report update failed: ${res.status}`);
            console.log('[iPlanner] ✅ Advisor report overwritten');
            return await res.json();
        } else {
            const folderId = await this.ensureFolderPath(token);
            const metadata = { name: ADVISOR_REPORT_FILE_NAME, parents: [folderId], mimeType: 'application/json' };
            const multipart =
                `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
                `${JSON.stringify(metadata)}\r\n` +
                `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
                `${body}\r\n` +
                `--${boundary}--`;
            const res = await fetch(
                'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
                {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': `multipart/related; boundary=${boundary}` },
                    body: multipart,
                }
            );
            if (!res.ok) throw new Error(`Advisor report create failed: ${res.status}`);
            const created = await res.json();
            localStorage.setItem(LS_ADVISOR_FILE_ID, created.id);
            console.log(`[iPlanner] 📄 Advisor report created id=${created.id}`);
            return created;
        }
    },

    // Create a timestamped backup snapshot in the same iPlanner folder.
    // File name: iplanner-backup-YYYY-MM-DD_HH-MM-SS.json
    // Each backup is a separate file — never overwrites previous backups.
    async writeBackupFile(token, data) {
        const folderId = await this.ensureFolderPath(token);
        const now = new Date();
        const pad = n => String(n).padStart(2, '0');
        const timestamp =
            `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
            `_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
        const fileName = `iplanner-backup-${timestamp}.json`;
        const body = JSON.stringify({ ...data, backupDate: now.toISOString() }, null, 2);
        const boundary = 'iPlanner_backup_boundary_z9q';
        const metadata = { name: fileName, parents: [folderId], mimeType: 'application/json' };
        const multipart =
            `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
            `${JSON.stringify(metadata)}\r\n` +
            `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
            `${body}\r\n` +
            `--${boundary}--`;
        const res = await fetch(
            'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': `multipart/related; boundary=${boundary}`,
                },
                body: multipart,
            }
        );
        if (!res.ok) throw new Error(`Backup upload failed: ${res.status}`);
        const created = await res.json();
        return { id: created.id, name: fileName, createdTime: now.toISOString() };
    },

    // Write (or overwrite) a monthly budget performance report.
    // File name: iplanner-budget-YYYY-MM.json  (one per cycle, overwrites if re-exported)
    async writeBudgetPerformanceFile(token, reportData) {
        const body = JSON.stringify(reportData, null, 2);
        const boundary = 'iPlanner_budget_boundary_b4r';
        const fileName = `iplanner-budget-${reportData.cycleId}.json`;
        const folderId = await this.ensureFolderPath(token);

        // Search for existing file with the same cycle name
        const q = encodeURIComponent(
            `name='${fileName}' and '${folderId}' in parents and trashed=false`
        );
        const searchRes = await fetch(
            `https://www.googleapis.com/drive/v3/files?q=${q}&fields=files(id)&spaces=drive`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!searchRes.ok) throw new Error(`Budget report search failed: ${searchRes.status}`);
        const { files } = await searchRes.json();

        if (files && files.length > 0) {
            // Overwrite
            const res = await fetch(
                `https://www.googleapis.com/upload/drive/v3/files/${files[0].id}?uploadType=media`,
                {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body,
                }
            );
            if (!res.ok) throw new Error(`Budget report update failed: ${res.status}`);
            console.log(`[iPlanner] ✅ Budget report overwritten: ${fileName}`);
            return await res.json();
        } else {
            // Create
            const metadata = { name: fileName, parents: [folderId], mimeType: 'application/json' };
            const multipart =
                `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
                `${JSON.stringify(metadata)}\r\n` +
                `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
                `${body}\r\n` +
                `--${boundary}--`;
            const res = await fetch(
                'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': `multipart/related; boundary=${boundary}`,
                    },
                    body: multipart,
                }
            );
            if (!res.ok) throw new Error(`Budget report create failed: ${res.status}`);
            const created = await res.json();
            console.log(`[iPlanner] 📄 Budget report created: ${fileName} id=${created.id}`);
            return created;
        }
    },

    // List all backup snapshots in the iPlanner folder, newest first.
    async listBackupFiles(token) {
        const folderId = await this.ensureFolderPath(token);
        const q = encodeURIComponent(
            `name contains 'iplanner-backup-' and '${folderId}' in parents and trashed=false`
        );
        const res = await fetch(
            `https://www.googleapis.com/drive/v3/files?q=${q}` +
            `&fields=files(id,name,createdTime,size)&orderBy=createdTime%20desc&spaces=drive`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`Backup list failed: ${res.status}`);
        const { files } = await res.json();
        return files || [];
    },
};
